import { useState, useRef, useEffect } from 'react';
import { Users, Package, Star, Mail, MapPin, Database, Palette, Megaphone, Send, Bell, Award, Target, Plane, Building2, MessageSquare, ChevronLeft, ChevronRight, Cloud } from 'lucide-react';

const AdminTabNav = ({ activeTab, setActiveTab }) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  
  const tabs = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'locations-map', label: 'Locations & Map', icon: MapPin },
    { id: 'dive-centers', label: 'Dive Center Accounts', icon: Building2 },
    { id: 'timeline', label: 'Timeline', icon: null },
    { id: 'packages', label: 'Packages', icon: Package },
    { id: 'reviews', label: 'Reviews', icon: Star },
    { id: 'invites', label: 'Invites', icon: Mail },
    { id: 'reports', label: 'Dive Reports', icon: MapPin },
    { id: 'trips', label: 'Trips', icon: Plane },
    { id: 'chat-forum', label: 'Chat/Forum', icon: MessageSquare },
    { id: 'announcements', label: 'Announcements', icon: Megaphone },
    { id: 'newsletter', label: 'Newsletter', icon: Send },
    { id: 'badges', label: 'Badges', icon: Award },
    { id: 'community', label: 'Community', icon: Target },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'theme', label: 'Theme Manager', icon: Palette },
    { id: 'export', label: 'Data Export', icon: Database },
    { id: 'cloud-storage', label: 'Cloud Storage', icon: Cloud },
  ];

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (scrollRef.current && activeTab) {
      const activeButton = scrollRef.current.querySelector(`[data-tab-id="${activeTab}"]`);
      if (activeButton) {
        activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [activeTab]);

  return (
    <div className="relative mb-8">
      {showLeftArrow && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-0 bottom-0 z-10 bg-gradient-to-r from-white via-white to-transparent px-2 flex items-center"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
      )}
      
      <div 
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-1 border-b border-gray-200 overflow-x-auto scrollbar-hide pb-px"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-tab-id={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === tab.id
                  ? 'text-cyan-600 border-b-2 border-cyan-600 -mb-px'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-t'
              }`}
            >
              {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
      
      {showRightArrow && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-0 bottom-0 z-10 bg-gradient-to-l from-white via-white to-transparent px-2 flex items-center"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      )}
    </div>
  );
};

export default AdminTabNav;
